import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import Footer from '../components/Footer';
import { Lock, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const validatePassword = () => {
        if (password.length < 8) {
            return 'Password must be at least 8 characters';
        }
        if (password !== confirmPassword) {
            return 'Passwords do not match';
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validationError = validatePassword();
        if (validationError) {
            toast.error(validationError);
            return;
        }

        setLoading(true);
        setError('');

        try {
            await authAPI.resetPassword(token, password);
            setSuccess(true);
            toast.success('Password reset successfully!');
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to reset password. The link may have expired.';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    // No token provided
    if (!token) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <div className="fixed top-4 right-4 z-50">
                    <ThemeToggle />
                </div>
                <div className="flex-1 flex items-center justify-center px-4 py-12">
                    <div className="w-full max-w-md text-center">
                        <Link to="/" className="flex items-center justify-center mb-8">
                            <Logo variant="full" size="large" />
                        </Link>
                        <Card className="bg-card border-border">
                            <CardContent className="pt-6">
                                <div className="flex justify-center mb-4">
                                    <div className="rounded-full bg-red-500/10 p-4">
                                        <XCircle className="h-12 w-12 text-red-500" />
                                    </div>
                                </div>
                                <h2 className="text-xl font-bold mb-2">Invalid Reset Link</h2>
                                <p className="text-muted-foreground mb-6">
                                    This password reset link is invalid or has expired.
                                </p>
                                <Button asChild className="w-full">
                                    <Link to="/forgot-password">Request New Link</Link>
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="flex items-center justify-center mb-8">
                        <Logo variant="full" size="large" />
                    </Link>

                    <Card className="bg-card border-border">
                        <CardHeader className="text-center">
                            <CardTitle className="font-heading font-bold text-2xl text-foreground">
                                {success ? 'Password Reset!' : 'Create New Password'}
                            </CardTitle>
                            <CardDescription>
                                {success 
                                    ? 'Your password has been successfully reset.'
                                    : 'Enter your new password below'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {success ? (
                                <div className="text-center space-y-6">
                                    <div className="flex justify-center">
                                        <div className="rounded-full bg-emerald-500/10 p-4">
                                            <CheckCircle className="h-12 w-12 text-emerald-500" />
                                        </div>
                                    </div>
                                    <p className="text-muted-foreground">
                                        You can now sign in with your new password.
                                    </p>
                                    <Button 
                                        className="w-full h-12" 
                                        onClick={() => navigate('/login')}
                                        data-testid="reset-password-login-btn"
                                    >
                                        Go to Sign In
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {error && (
                                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                                            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="password">New Password</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="Minimum 8 characters"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="pl-10 pr-10 bg-accent border-border h-12"
                                                required
                                                minLength={8}
                                                data-testid="reset-password-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword">Confirm Password</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="confirmPassword"
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="Confirm your password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="pl-10 bg-accent border-border h-12"
                                                required
                                                data-testid="reset-password-confirm-input"
                                            />
                                        </div>
                                    </div>

                                    {password && confirmPassword && password !== confirmPassword && (
                                        <p className="text-sm text-red-500">Passwords do not match</p>
                                    )}

                                    <Button
                                        type="submit"
                                        className="w-full h-12 text-base active:scale-95 transition-transform"
                                        disabled={loading || password.length < 8 || password !== confirmPassword}
                                        data-testid="reset-password-submit-btn"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Resetting...
                                            </>
                                        ) : (
                                            'Reset Password'
                                        )}
                                    </Button>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
            
            <Footer />
        </div>
    );
};

export default ResetPassword;
